import React from "react";
import { useSubscribeDetailsByIdQuery } from "../../redux/features/user/userApi";

const SubsHis = ({ id }) => {
  const { data, isLoading, isError, error, refetch } =
    useSubscribeDetailsByIdQuery(id);

  if (isLoading) {
    return <div className="py-6 text-center">Loading subscription details...</div>;
  }

  if (isError) {
    return (
      <div className="py-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          <p className="font-semibold mb-2">Failed to load subscription details</p>
          <p className="text-sm">{error?.data?.message || "Unexpected error"}</p>
          <button
            className="mt-3 px-3 py-1.5 bg-red-600 text-white rounded"
            onClick={() => refetch()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const subscriptions = data?.data || [];
  if (!subscriptions.length) {
    return <div className="py-6 text-gray-600">No subscription records found.</div>;
  }

  const rows = Array.isArray(subscriptions) ? subscriptions : [subscriptions];

  // ✅ Remaining Days (auto updates daily)
  const computeRemainingDays = (endDate) => {
    if (!endDate) return "-";
    const today = new Date();
    const end = new Date(endDate);
    const diff = Math.ceil((end - today) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 0;
  };

  // ✅ Format allocations nicely
  const formatAllocations = (allocations) => {
    if (!allocations) return "-";
    return Object.entries(allocations).map(([coin, data]) => (
      <div key={coin} className="text-xs">
        <strong>{coin.toUpperCase()}</strong> → USD: {data.usd}, Amount:{" "}
        {data.amount}, Price: {data.price}
      </div>
    ));
  };

  const columns = [
    "planname",
    "roi",
    "term",
    "reward",
    "planquote",
    "startdate",
    "enddate",
    "remainingdays", // 👈 new column
    "redeemed",
    "allocations",
  ];

 const renderCell = (row, key) => {
  if (key === "remainingdays") {
    return `${computeRemainingDays(row.enddate)} Days`;
  }

  if (key === "allocations") {
    return formatAllocations(row.allocations);
  }

  if (key === "redeemed") {
    const value = row.Redeemed ?? row.redeemed;
    return value === true ? "True" : value === false ? "False" : "-";
  }

  return row[key] ?? "-";
};


  // ✅ CSV Export
  const handleExportCsv = () => {
    const header = columns.join(",");

    const dataLines = rows.map((row) =>
      columns
        .map((key) => {
          if (key === "remainingdays") {
            return `"${computeRemainingDays(row.enddate)} Days"`;
          }
          if (key === "allocations") {
            const allocText = Object.entries(row.allocations || {})
              .map(
                ([coin, d]) =>
                  `${coin.toUpperCase()}(USD:${d.usd},Amount:${d.amount},Price:${d.price})`
              )
              .join(" | ");
            return `"${allocText}"`;
          }
          return `"${row[key] ?? "-"}"`;
        })
        .join(",")
    );

    const csv = [header, ...dataLines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `subscriptions_${id || "user"}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="w-full px-2 sm:px-4 md:px-6">

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-center mt-4 p-4 bg-white border rounded shadow">
        <h1 className="text-lg sm:text-2xl font-bold text-blue-900">
          Subscribe Details
        </h1>

        <button
          onClick={handleExportCsv}
          className="mt-2 sm:mt-0 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Export CSV
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto mt-4">
        <table className="min-w-full border border-gray-200 rounded">
          <thead className="bg-gray-100">
            <tr>
              {columns.map((key) => (
                <th
                  key={key}
                  className="px-3 py-2 text-xs sm:text-sm font-semibold border-b whitespace-nowrap"
                >
                  {key.replace(/_/g, " ").toUpperCase()}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {rows.map((row, idx) => (
              <tr key={idx} className="odd:bg-white even:bg-gray-50">
                {columns.map((key) => (
                  <td
                    key={key}
                    className="px-3 py-2 text-xs sm:text-sm border-b align-top"
                  >
                    {renderCell(row, key)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

    </div>
  );
};

export default SubsHis;
